import React, { useState, useEffect } from 'react';
import { Button } from './Button';
import { Copy, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

interface CopyButtonProps {
  onCopy: () => Promise<void> | void;
  className?: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  showText?: boolean;
  title?: string;
}

export const CopyButton: React.FC<CopyButtonProps> = ({
  onCopy,
  className,
  variant = 'outline',
  size = 'sm',
  showText = false,
  title = 'Copy to clipboard',
}) => {
  const [copied, setCopied] = useState(false);

  const handleClick = async () => {
    if (copied) return;

    try {
      await onCopy();
      setCopied(true);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => {
        setCopied(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [copied]);

  return (
    <Button
      onClick={handleClick}
      variant={variant}
      size={size}
      className={cn(
        'relative ',
        copied && '!bg-green-500 hover:!bg-green-500 text-white border-green-500',
        className
      )}
      title={title}
    >
      <span className={cn('flex items-center gap-2', copied && 'scale-110')}>
        {copied ? (
          <>
            <Check className={cn('w-4 h-4')} />
            {showText && <span className="animate-in fade-in slide-in-from-left-1">Copied!</span>}
          </>
        ) : (
          <>
            <Copy className="w-4 h-4" />
            {showText && <span>Copy</span>}
          </>
        )}
      </span>
    </Button>
  );
};

export default CopyButton;
